import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import axiosInstance from '../../api/axiosConfig';
import {
  Box, Typography, Skeleton, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Grid, Card, CardMedia, useTheme, IconButton,
  Stack, Snackbar, Alert, Slide, CircularProgress,
  Chip, Autocomplete, alpha, ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';
import { styled } from '@mui/material/styles';
import Lottie from 'lottie-react';
import animationData from '../../assets/icons/infinate_loader.json';
import PageHeader from '../common/PageHeader';
import PageContainer from '../common/PageContainer';
import EmptyState from '../common/EmptyState';
import AdminPageHero from '../common/AdminPageHero';

// --- Constants ---
const MAX_UPLOAD_COUNT = 5;
const MAX_FILE_SIZE_MB = 1;
const OTHER_CATEGORY_NAME = "Other Images"; // Consistent name for uncategorized

// --- Styled Components ---
const GalleryCard = styled(Card)(({ theme }) => ({
    position: 'relative',
    height: '100%',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out',
    borderRadius: 0,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.04),
    },
    '& .delete-btn-container': {
        position: 'absolute', top: theme.spacing(0.5), right: theme.spacing(0.5), zIndex: 2,
        backgroundColor: alpha(theme.palette.common.black, 0.5),
        borderRadius: 0, opacity: 0, transition: 'opacity 0.2s',
        '&:hover': { backgroundColor: alpha(theme.palette.error.dark, 0.7) }
    },
    '&:hover .delete-btn-container': { opacity: 1 },
}));

const FolderCard = styled(Card)(({ theme }) => ({
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    cursor: 'pointer',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[2],
        borderColor: theme.palette.primary.light,
    },
}));

const SquareCardMedia = styled(CardMedia)(({ theme }) => ({
  display: 'block', // Ensure it behaves like a block element
  objectFit: 'cover',
  aspectRatio: '1 / 1', // Maintain square aspect ratio
  backgroundColor: theme.palette.action.hover,
}));

const UploadButton = styled(Button)(({ theme }) => ({
    borderRadius: 0,
    padding: theme.spacing(0.75, 2),
    fontWeight: 700,
}));

const UploadDialogContentStyled = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(1) + ' !important',
}));

const UploadArea = styled(Box)(({ theme, isDraggingOver }) => ({
    padding: theme.spacing(3), textAlign: 'center',
    border: `2px dashed ${isDraggingOver ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: 0,
    backgroundColor: isDraggingOver ? alpha(theme.palette.primary.light, 0.1) : alpha(theme.palette.grey[50], 0.5),
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.light, 0.05),
        borderColor: theme.palette.primary.light,
    },
    minHeight: 150, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
}));

const PreviewThumbnail = styled(Box)(({ theme }) => ({
    position: 'relative', margin: theme.spacing(0.5),
    '& img': {
        display: 'block', width: 70, height: 70, objectFit: 'cover',
        borderRadius: 0,
        border: `1px solid ${theme.palette.divider}`,
    },
    '& .remove-button': {
        position: 'absolute', top: -6, right: -6,
        backgroundColor: alpha(theme.palette.common.black, 0.6), color: theme.palette.common.white,
        padding: '2px', minWidth: 0, width: 18, height: 18,
        '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.8) }
    }
}));

const DeleteConfirmationDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': { borderRadius: 0, padding: theme.spacing(1.5) },
}));

// --- Main Component ---
const ImageGallery = () => {
  const [images, setImages] = useState([]); // Raw list of all images
  const [resolvedUrls, setResolvedUrls] = useState({});
  const [categories, setCategories] = useState([]); // Stores { id, name }
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [openLightbox, setOpenLightbox] = useState(false);
  const [viewMode, setViewMode] = useState('folders'); // 'folders' | 'grid'

  // Upload State
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFilePreviews, setSelectedFilePreviews] = useState([]);
  const [newImageCategoryInput, setNewImageCategoryInput] = useState(null);
  const [newImageDescription, setNewImageDescription] = useState('');
  const [uploadError, setUploadError] = useState('');

  // General State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDeleteId, setImageToDeleteId] = useState(null);
  const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [activeFolder, setActiveFolder] = useState(null);

  const theme = useTheme();
  const fileInputRef = useRef(null);
  const signedUrlInFlight = useRef(new Set());

  // --- Snackbar Utility ---
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbarMessage(message); setSnackbarSeverity(severity); setSnackbarOpen(true);
  }, []);

  // --- Data Fetching ---
  const fetchGalleryData = useCallback(async () => {
    setLoading(true);
    try {
      const [imagesRes, categoriesRes] = await Promise.all([
        axiosInstance.get('/api/images/all'),
        axiosInstance.get('/api/images/categories'),
      ]);
      const imagesData = imagesRes.data || [];
      const validCategories = (categoriesRes.data || []).filter(cat => cat && cat.name && cat.name.trim() !== "");
      setCategories(validCategories);
      setImages(imagesData);
    } catch (err) {
      console.error('Error fetching gallery data:', err);
      showSnackbar('Error fetching gallery data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => { fetchGalleryData(); }, [fetchGalleryData]);

  // --- Derived Data for Display (Memoized) ---

  // 1. Flat list, sorted latest first (for grid view and lightbox)
  const sortedAllImages = useMemo(() => {
    return [...images].sort((a, b) =>
      new Date(b.uploadedAt || b.createdAt || 0) - new Date(a.uploadedAt || a.createdAt || 0)
    );
  }, [images]);

  const folderCards = useMemo(() => {
    const counts = images.reduce((acc, img) => {
      const key = img.categoryId ? String(img.categoryId) : 'other';
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const base = categories.map((cat) => ({
      key: `cat-${cat.id}`,
      categoryId: cat.id,
      categoryName: cat.name,
      count: counts[String(cat.id)] || 0,
    }));

    const cards = [
      { key: 'all', categoryId: null, categoryName: 'All Photos', count: images.length },
      ...base,
    ];

    if (counts.other) {
      cards.push({ key: 'other', categoryId: null, categoryName: OTHER_CATEGORY_NAME, count: counts.other });
    }

    return cards;
  }, [images, categories]);

  const activeImages = useMemo(() => {
    if (!activeFolder) return [];
    if (activeFolder.key === 'all') return sortedAllImages;
    if (activeFolder.key === 'other') {
      return sortedAllImages.filter((img) => !img.categoryId);
    }
    return sortedAllImages.filter((img) => String(img.categoryId) === String(activeFolder.categoryId));
  }, [activeFolder, sortedAllImages]);

  // --- Lightbox ---
  const getImageSrc = useCallback(
    (img) => resolvedUrls[img.id] || img.fileUrl || '/placeholder.jpg',
    [resolvedUrls]
  );

  const slides = useMemo(() => activeImages.map(img => ({
    src: getImageSrc(img), title: img.description || '', id: img.id,
  })), [activeImages, getImageSrc]);

  const handleImageError = useCallback(
    async (img) => {
      if (!img?.id || !img?.fileUrl) return;
      if (resolvedUrls[img.id]) return;
      if (signedUrlInFlight.current.has(img.id)) return;
      signedUrlInFlight.current.add(img.id);
      try {
        const resp = await axiosInstance.get('/api/images/signed-url', {
          params: { file: img.fileUrl, expiresInMinutes: 60 },
        });
        const signedUrl = resp?.data?.signedUrl;
        if (signedUrl) {
          setResolvedUrls((prev) => ({ ...prev, [img.id]: signedUrl }));
          return;
        }
      } catch (err) {
        console.error('Failed to get signed URL', err);
      } finally {
        signedUrlInFlight.current.delete(img.id);
      }
      setResolvedUrls((prev) => ({ ...prev, [img.id]: '/placeholder.jpg' }));
    },
    [resolvedUrls]
  );

  const handleImageClick = (imageId) => {
     // Lightbox *always* uses the flat sorted list (`slides`)
     const index = slides.findIndex(slide => slide.id === imageId);
     if (index !== -1) { setLightboxIndex(index); setOpenLightbox(true); }
     else { console.warn("Could not find image index for lightbox."); }
  };
  const handleLightboxClose = () => { setLightboxIndex(-1); setOpenLightbox(false); };

  // --- Upload Dialog Logic ---
  const resetUploadDialog = useCallback(() => { /* ... same as before ... */
    selectedFilePreviews.forEach(url => URL.revokeObjectURL(url));
    setSelectedFiles([]); setSelectedFilePreviews([]);
    setNewImageCategoryInput(null); setNewImageDescription('');
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = "";
   }, [selectedFilePreviews]); // Keep dependency
  const handleOpenUploadDialog = () => {
    resetUploadDialog();
    if (activeFolder?.categoryId) {
      const match = categories.find((cat) => String(cat.id) === String(activeFolder.categoryId));
      if (match) {
        setNewImageCategoryInput(match);
      }
    }
    setUploadDialogOpen(true);
  };
  const handleCloseUploadDialog = () => { resetUploadDialog(); setUploadDialogOpen(false); };
  const handleFileSelection = (selectedFileList) => { /* ... same as before ... */
    setUploadError('');
    const newFiles = Array.from(selectedFileList);
    let currentFileCount = selectedFiles.length;
    let addedFiles = [], addedPreviews = [], errors = [];

    newFiles.forEach(file => {
      if (currentFileCount >= MAX_UPLOAD_COUNT) { errors.push(`Max ${MAX_UPLOAD_COUNT}.`); return; }
      if (!file.type.startsWith("image/")) { errors.push(`"${file.name}" invalid.`); return; }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) { errors.push(`"${file.name}" > ${MAX_FILE_SIZE_MB}MB.`); return; }
      if (selectedFiles.some(f => f.name === file.name && f.size === file.size)) { errors.push(`"${file.name}" dup.`); return; }
      addedFiles.push(file); addedPreviews.push(URL.createObjectURL(file)); currentFileCount++;
    });

    if (errors.length > 0) setUploadError(errors.join(' '));
    setSelectedFiles(prev => [...prev, ...addedFiles]);
    setSelectedFilePreviews(prev => [...prev, ...addedPreviews]);
  };
  const handleManualFileSelect = (e) => handleFileSelection(e.target.files);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e) => { e.preventDefault(); handleFileSelection(e.dataTransfer.files); };
  const handleRemoveSelectedFile = (indexToRemove) => { /* ... same as before ... */
    URL.revokeObjectURL(selectedFilePreviews[indexToRemove]);
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setSelectedFilePreviews(prev => prev.filter((_, index) => index !== indexToRemove));
   };

  // --- Upload Action ---
  const handleUpload = async () => { /* ... same logic as before ... */
    setUploadError('');
    if (selectedFiles.length === 0) { setUploadError('Please select image(s).'); return; }
    if (!newImageDescription) { setUploadError('Please provide a description.'); return; }
    let categoryValueToSend = '';
    if (newImageCategoryInput) {
        if (typeof newImageCategoryInput === 'object' && newImageCategoryInput.id) {
             categoryValueToSend = newImageCategoryInput.id;
         } else if (typeof newImageCategoryInput === 'string' && newImageCategoryInput.trim() !== '') {
             categoryValueToSend = newImageCategoryInput.trim();
         }
    }

    setUploading(true);
    const formData = new FormData();
    if (categoryValueToSend) {
      if (String(categoryValueToSend).match(/^\d+$/)) {
        formData.append('categoryId', String(categoryValueToSend));
      } else {
        formData.append('categoryName', String(categoryValueToSend));
      }
    }
    formData.append('description', newImageDescription);
    selectedFiles.forEach(file => formData.append('image', file));

    try {
      const resp = await axiosInstance.post('/api/images/upload/by-category', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (resp.status === 200 || resp.status === 201) {
        showSnackbar('Images uploaded successfully!', 'success');
        handleCloseUploadDialog();
        await fetchGalleryData();
      } else {
        throw new Error(resp.data?.message || `Upload failed: ${resp.status}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.message || error.message || 'Unknown upload error.';
      setUploadError(`Upload failed: ${message}`);
      showSnackbar(`Upload failed. ${message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  // --- Delete Logic ---
  const handleDeleteConfirmation = (imageId) => { setImageToDeleteId(imageId); setDeleteDialogOpen(true); };
  const handleDeleteDialogClose = () => { setDeleteDialogOpen(false); setImageToDeleteId(null); };
  const confirmDeleteImage = async () => { /* ... same as before ... */
    if (!imageToDeleteId) return;
    try {
      await axiosInstance.delete(`/api/images/${imageToDeleteId}`);
      showSnackbar('Image deleted successfully!', 'success');
      setImages(prev => prev.filter(img => img.id !== imageToDeleteId));
    } catch (error) {
      console.error('Error deleting image:', error);
      showSnackbar('Error deleting image.', 'error');
    } finally {
      handleDeleteDialogClose();
    }
  };

  const handleOpenDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setDeleteCategoryDialogOpen(true);
  };

  const handleCloseDeleteCategoryDialog = () => {
    if (deletingCategory) return;
    setDeleteCategoryDialogOpen(false);
    setCategoryToDelete(null);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete?.categoryId) return;
    setDeletingCategory(true);
    try {
      await axiosInstance.delete(`/api/images/categories/${categoryToDelete.categoryId}`, {
        params: { deleteImages: true },
      });
      showSnackbar(`Folder "${categoryToDelete.categoryName}" deleted.`, 'success');
      handleCloseDeleteCategoryDialog();
      await fetchGalleryData();
    } catch (error) {
      console.error('Error deleting folder:', error);
      showSnackbar('Error deleting folder.', 'error');
    } finally {
      setDeletingCategory(false);
    }
  };

  // --- Snackbar ---
  const handleCloseSnackbar = (event, reason) => { if (reason === 'clickaway') return; setSnackbarOpen(false); };

  // --- View Mode ---
  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode === null) return;
    setViewMode(newViewMode);
    if (newViewMode === 'folders') {
      setActiveFolder(null);
    }
    if (newViewMode === 'grid') {
      setActiveFolder({ key: 'all', categoryName: 'All Photos' });
    }
  };

  // --- Cleanup Object URLs ---
  useEffect(() => {
    // This effect runs when the component unmounts
    return () => {
      selectedFilePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [selectedFilePreviews]); // Rerun if previews change (though cleanup only matters on unmount)


  // --- Render Gallery Grid Item --- (Helper for cleaner mapping)
  const renderGridItem = (img) => (
    <Grid item xs={6} sm={4} md={3} lg={2} xl={2} key={img.id}>
        <GalleryCard elevation={1} onClick={() => handleImageClick(img.id)}>
            <Box className="delete-btn-container">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteConfirmation(img.id); }} aria-label="delete image" sx={{ color: 'white', p: '4px' }}>
                    <DeleteIcon sx={{ fontSize: '1rem' }}/>
                </IconButton>
            </Box>
            <SquareCardMedia
              component="img"
              image={getImageSrc(img)}
              alt={img.description || 'Gallery image'}
              onError={() => handleImageError(img)}
            />
            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 100%)', p: 0.8, opacity: 0, transition: 'opacity 0.2s ease-in-out', '&:hover': { opacity: 1 } }}>
                <Typography variant="caption" color="white" noWrap sx={{ display: 'block', textAlign: 'center' }}>{img.description || `Image ${img.id}`}</Typography>
            </Box>
        </GalleryCard>
    </Grid>
  );

  const renderFolderCard = (folder) => (
    <Grid item xs={12} sm={6} md={4} lg={3} key={folder.key}>
      <FolderCard onClick={() => setActiveFolder(folder)}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 46, height: 46, borderRadius: 6, bgcolor: alpha(theme.palette.primary.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FolderRoundedIcon color="primary" />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {folder.categoryName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {folder.count} photos
            </Typography>
          </Box>
          <Chip label={folder.count} size="small" variant="outlined" />
        </Box>
      </FolderCard>
    </Grid>
  );

  const headerActions = (
    <Stack direction="row" spacing={1.1} alignItems="center">
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        aria-label="Gallery view mode"
        size="small"
      >
        <ToggleButton value="folders" aria-label="Folders view">
          <Tooltip title="Folders">
            <ViewListIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="grid" aria-label="All photos view">
          <Tooltip title="All Photos">
            <GridViewIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      <UploadButton
        variant="contained"
        onClick={handleOpenUploadDialog}
        startIcon={<AddPhotoAlternateIcon />}
        size="small"
      >
        Upload Photos
      </UploadButton>
    </Stack>
  );

  return (
    <PageContainer sx={{ width: '100%' }}>
      <PageHeader
        title="Photo Gallery"
        subtitle="Upload, organize, and browse school photos by folder or event."
        actions={headerActions}
      />
      <AdminPageHero
        title="Photo Gallery"
        subtitle="Upload, organize, and browse school photos by folder or event."
        actions={headerActions}
        metrics={[
          { label: 'Total Photos', value: images.length },
          { label: 'Folders', value: folderCards.filter((folder) => folder.key !== 'all').length },
          { label: 'View', value: viewMode === 'folders' ? 'Folders' : 'Grid' },
        ]}
      />

      {/* Main Gallery Area */}
      <Box sx={{ pt: 1 }}>
        {loading ? (
          <Grid container spacing={2}>
            {[...Array(12)].map((_, index) => (
              <Grid item xs={6} sm={4} md={3} lg={2} xl={2} key={index}>
                <Skeleton variant="rectangular" sx={{ aspectRatio: '1/1', borderRadius: 0 }} />
              </Grid>
            ))}
          </Grid>
        ) : images.length === 0 ? (
          <EmptyState
            title="No photos in the gallery"
            description="Upload event images to start building the media library."
            action={(
              <UploadButton
                variant="contained"
                onClick={handleOpenUploadDialog}
                startIcon={<AddPhotoAlternateIcon />}
                size="small"
              >
                Upload Photos
              </UploadButton>
            )}
          />
        ) : (
          viewMode === 'folders' ? (
            activeFolder ? (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => setActiveFolder(null)}
                    >
                      Back to folders
                    </Button>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        {activeFolder.categoryName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {activeImages.length} photos
                      </Typography>
                    </Box>
                  </Stack>
                  <UploadButton
                    variant="contained"
                    onClick={handleOpenUploadDialog}
                    startIcon={<AddPhotoAlternateIcon />}
                    size="small"
                  >
                    Upload Photos
                  </UploadButton>
                </Stack>
                {activeImages.length === 0 ? (
                  <EmptyState
                    title="No photos in this folder"
                    description="Upload photos to start building this event."
                    action={(
                      <UploadButton
                        variant="contained"
                        onClick={handleOpenUploadDialog}
                        startIcon={<AddPhotoAlternateIcon />}
                        size="small"
                      >
                        Upload Photos
                      </UploadButton>
                    )}
                  />
                ) : (
                  <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
                    {activeImages.map(renderGridItem)}
                  </Grid>
                )}
              </Stack>
            ) : (
              <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
                {folderCards.map(renderFolderCard)}
              </Grid>
            )
          ) : (
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
               {sortedAllImages.map(renderGridItem)}
            </Grid>
          )
        )}
      </Box>

      {/* --- Dialogs & Snackbar --- */}

      {/* Lightbox */}
      {openLightbox && slides.length > 0 && (
          <Lightbox
              open={openLightbox} close={handleLightboxClose} slides={slides} index={lightboxIndex}
              plugins={[Captions]} captions={{ descriptionTextAlign: 'center', descriptionMaxLines: 3 }}
              styles={{
                container: { backgroundColor: alpha(theme.palette.common.black, 0.95) },
                slide: { padding: 0 },
                image: { maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' },
              }}
          />
       )}

      {/* Upload Dialog */}
       <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 0 } }}>
          <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', pb: 1, fontSize: '1.4rem' }}>Upload New Photos</DialogTitle>
          <IconButton aria-label="close" onClick={handleCloseUploadDialog} sx={{ position: 'absolute', right: 12, top: 12, color: 'grey.500' }} disabled={uploading}> <CloseIcon /> </IconButton>
          <UploadDialogContentStyled dividers>
              {uploading ? ( /* ... Uploading Lottie ... */
                   <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 350 }}>
                      <Lottie animationData={animationData} style={{ height: 150, width: 150 }} loop={true} />
                      <Typography sx={{ mt: 2 }} variant="h6">Uploading photos...</Typography>
                  </Box>
              ) : (
                  <Stack spacing={2}>
                       {uploadError && <Alert severity="error" sx={{ width: '100%' }} onClose={() => setUploadError('')}>{uploadError}</Alert>}
                       {selectedFilePreviews.length > 0 && (
                           <Box sx={{ p: 1, maxHeight: 180, overflowY: 'auto', bgcolor: 'action.hover', border: `1px solid ${theme.palette.divider}` }}>
                               <Grid container spacing={1}>
                                   {selectedFilePreviews.map((previewUrl, index) => (
                                       <Grid item key={previewUrl}>
                                            <PreviewThumbnail>
                                                <img src={previewUrl} alt={`Preview ${index + 1}`} />
                                                <IconButton size="small" onClick={() => handleRemoveSelectedFile(index)} className="remove-button" aria-label={`Remove image ${index + 1}`}>
                                                    <CancelIcon sx={{ fontSize: '1rem' }} />
                                                </IconButton>
                                            </PreviewThumbnail>
                                       </Grid>
                                   ))}
                               </Grid>
                           </Box>
                       )}
                      <UploadArea onClick={() => fileInputRef.current?.click()} onDragOver={handleDragOver} onDrop={handleDrop} > 
                           <AddPhotoAlternateIcon sx={{ fontSize: 40, color: theme.palette.action.active, mb: 1 }} />
                           <Typography variant="body1" color="text.secondary" fontWeight="medium">Drag & Drop or Click to Select</Typography>
                           <Typography variant="caption" color="text.secondary">Up to {MAX_UPLOAD_COUNT} images, Max {MAX_FILE_SIZE_MB}MB each</Typography>
                           {selectedFiles.length > 0 && <Chip label={`${selectedFiles.length} / ${MAX_UPLOAD_COUNT} selected`} size="small" sx={{ mt: 1.5 }} color="primary" variant="outlined"/> }
                      </UploadArea>
                      <input ref={fileInputRef} id="image-upload-input-multi" hidden accept="image/*" type="file" multiple onChange={handleManualFileSelect}/>
                      <Autocomplete freeSolo options={categories.filter(cat => cat.id !== 'all')} getOptionLabel={(option) => option.name || option} value={newImageCategoryInput} onChange={(event, newValue) => setNewImageCategoryInput(newValue)} renderInput={(params) => ( <TextField {...params} label="Event / Folder Name" placeholder="Select existing or type new" size="small" required={!newImageCategoryInput}/> )} />
                      <TextField fullWidth required size="small" label="Photos Description" placeholder="Describe the event or photos (applies to all selected)" multiline rows={3} value={newImageDescription} onChange={(e) => setNewImageDescription(e.target.value)}/>
                  </Stack>
              )}
          </UploadDialogContentStyled>
          <DialogActions sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
              <Button onClick={handleCloseUploadDialog} disabled={uploading} color="inherit" size="small">Cancel</Button>
              <Button variant="contained" disabled={selectedFiles.length === 0 || !newImageDescription || uploading} onClick={handleUpload} size="small" startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : null}>
                  {uploading ? `Uploading ${selectedFiles.length}...` : `Upload ${selectedFiles.length} Photo(s)`}
              </Button>
          </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog open={deleteDialogOpen} onClose={handleDeleteDialogClose} TransitionComponent={Slide} transitionDuration={300}>
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', pt: 2 }}>Confirm Delete</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography>Delete this image permanently?</Typography>
          <Typography variant="body2" color="textSecondary">This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={handleDeleteDialogClose} size="small" color="inherit">Cancel</Button>
          <Button variant="contained" onClick={confirmDeleteImage} color="error" size="small">Delete</Button>
        </DialogActions>
      </DeleteConfirmationDialog>

      <DeleteConfirmationDialog open={deleteCategoryDialogOpen} onClose={handleCloseDeleteCategoryDialog} TransitionComponent={Slide} transitionDuration={300}>
        <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center', pt: 2 }}>Delete Folder</DialogTitle>
        <DialogContent sx={{ textAlign: 'center' }}>
          <Typography>Delete folder "{categoryToDelete?.categoryName}" and all images inside?</Typography>
          <Typography variant="body2" color="textSecondary">This will remove all images from the gallery.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={handleCloseDeleteCategoryDialog} size="small" color="inherit" disabled={deletingCategory}>Cancel</Button>
          <Button variant="contained" onClick={confirmDeleteCategory} color="error" size="small" disabled={deletingCategory}>
            {deletingCategory ? 'Deleting...' : 'Delete Folder'}
          </Button>
        </DialogActions>
      </DeleteConfirmationDialog>

      {/* Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="standard" sx={{ width: '100%' }}>{snackbarMessage}</Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default ImageGallery;
